import React, { useEffect, useState } from 'react';
import { useQuery } from '@apollo/react-hooks';
import { useParams } from 'react-router-dom';
import { useInView } from 'react-intersection-observer';
import { useFeatureFlagState } from 'context';
import { getProfileActivityFeedQuery } from 'services/apollo/queries/profileQueries';
import { useTranslation } from 'hooks';

import { handleFetchMoreError, criticalFeedError } from 'services';

import { FeedListItem, FeedListInfinitySpinner, LoaderDashboard, EmptyFeed } from 'components';

import styles from './ProfileFeed.module.scss';

interface ProfileCommonFeedProps {
  isCurrentUser?: boolean;
}

export const ProfileCommonFeed: React.FC<ProfileCommonFeedProps> = ({ isCurrentUser }) => {
  const { t } = useTranslation();
  const { userId } = useParams();
  const { groupsFeatureFlag } = useFeatureFlagState();

  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const { error, data, fetchMore } = useQuery(getProfileActivityFeedQuery, {
    variables: { cursor: '', userId, includeGroups: groupsFeatureFlag },
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });

  const [ref, inView] = useInView({
    rootMargin: '500px 0px',
    threshold: 0,
  });

  /**
   * Loads more results into the activity feed
   */
  const triggerFetchMore = async () => {
    setIsLoadingMore(true);
    const { endCursor } = data.profileByIdentifier.userNodes.pageInfo;

    try {
      await fetchMore({
        variables: { cursor: endCursor },
        updateQuery: (prevResult: any, { fetchMoreResult }) => {
          fetchMoreResult.profileByIdentifier.userNodes.nodes = [
            ...prevResult.profileByIdentifier.userNodes.nodes,
            ...fetchMoreResult.profileByIdentifier.userNodes.nodes,
          ];

          setIsLoadingMore(false);
          return fetchMoreResult;
        },
      });
    } catch (e) {
      handleFetchMoreError(e);
    }
  };

  useEffect(() => {
    if (inView === true) {
      triggerFetchMore().then((response) => response);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inView]);

  if (data === undefined)
    return (
      <div className={styles.tetrisMain}>
        <LoaderDashboard layout="HeadlessFeed" />
      </div>
    );

  if (error && criticalFeedError(error))
    return (
      <div className={styles.tetrisMain}>
        <LoaderDashboard layout="HeadlessFeed" />
      </div>
    );

  const nodes = data?.profileByIdentifier?.userNodes?.nodes;
  const pageInfo = data?.profileByIdentifier?.userNodes?.pageInfo;

  if (nodes === undefined)
    return (
      <div className={styles.tetrisMain}>
        <LoaderDashboard layout="HeadlessFeed" />
      </div>
    );

  if (nodes?.length === 0)
    return (
      <div className={styles.tetrisMain}>
        <EmptyFeed
          message={
            isCurrentUser
              ? t('profile-screen.profile-feed.empty-feed-current-user')
              : t('profile-screen.profile-feed.empty-feed-common', {
                  name: data?.profileByIdentifier?.name,
                })
          }
        />
      </div>
    );

  return (
    <>
      {nodes?.map((node: any) => (
        <FeedListItem
          nodeId={node.id}
          nodeType={node.__typename}
          key={node.id}
          name={node.title}
          baseUrl={node.baseUrl}
          url={node.url}
          owner={node.owners[0]}
          collaborators={node.accessorInfo}
          modified={node.modified}
          created={node.created}
          nodeIcon={node.category}
          isIntegrationConnected={node.isIntegrationConnected}
          integrationType={node.integrationType}
          isShareable={node.sharingEnabled}
          shareTargets={node.shareTargets}
        />
      ))}

      {pageInfo?.hasNextPage && (
        <div ref={ref}>
          <FeedListInfinitySpinner isLoadingMore={isLoadingMore} />
        </div>
      )}
    </>
  );
};
